import * as React from 'react';
import { styled } from '@glitz/react';
import { timeout } from 'webapp/utils/timeout';
import { whenIdle } from 'webapp/utils/idle';
import { useIsMountedRef } from '@avensia/excite-ui';

const DURATION = 1000;

type RippleType = {
  key: number;
  top: number;
  left: number;
  radius: number;
};

export function useRipple() {
  const mountedRef = useIsMountedRef();
  const [ripples, setRipples] = React.useState<RippleType[]>([]);

  const push = React.useCallback(
    (target: HTMLElement, clientX: number, clientY: number) => {
      whenIdle(async () => {
        const { clientHeight, clientWidth } = target;
        const rect = target.getBoundingClientRect();
        const scaleX = rect.width / clientWidth;
        const scaleY = rect.height / clientHeight;
        const radius = Math.max(clientWidth, clientHeight);

        const props: RippleType = {
          key: Math.random(),
          radius,
          top: ((clientY - rect.top) / scaleY - radius) / clientHeight,
          left: ((clientX - rect.left) / scaleX - radius) / clientWidth,
        };

        if (mountedRef.current) {
          setRipples(ripples => [...ripples, props]);

          await timeout(DURATION);

          if (mountedRef.current) {
            setRipples(ripples => ripples.filter(ripple => ripple !== props));
          }
        }
      });
    },
    [mountedRef],
  );

  const nodes = React.useMemo(
    () =>
      ripples.map(({ key, top, left, radius }) => (
        <Ripple
          key={key}
          style={{
            top: `${top * 100}%`,
            left: `${left * 100}%`,
            width: `${radius * 2}px`,
            height: `${radius * 2}px`,
          }}
        />
      )),
    [ripples],
  );

  return [nodes, push] as const;
}

export const rippleContainerDecorator = styled({
  position: 'relative',
  overflow: 'hidden',
});

const Ripple = styled.span({
  position: 'absolute',
  willChange: 'opacity, transform',
  border: { y: { x: { radius: '50%' } } },
  backgroundColor: 'currentColor',
  pointerEvents: 'none',
  animation: {
    name: {
      from: {
        opacity: 0.1,
        transform: 'scale(0)',
      },
      to: {
        opacity: 0,
        transform: 'scale(1)',
      },
    },
    timingFunction: 'ease-out',
    duration: DURATION,
    fillMode: 'forwards',
  },
});
